import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

export interface TimelineSegment {
  key: string;
  label: string;
  /** Bundle day it starts on, from 0. */
  startDay: number;
  /** Bundle day it ends on, from 0: a tour's last day, or a hotel's check-out day. */
  endDay: number;
  color: 'primary' | 'secondary';
}

interface Props {
  segments: Array<TimelineSegment>;
}

// A one-day tour still needs a visible bar.
const MIN_LENGTH = 0.5;

/** Segments that don't overlap share a lane, so a tour then a hotel reads as one journey. */
function intoLanes(segments: Array<TimelineSegment>): Array<Array<TimelineSegment>> {
  const lanes: Array<Array<TimelineSegment>> = [];
  [...segments]
    .sort((a, b) => a.startDay - b.startDay)
    .forEach((segment) => {
      const lane = lanes.find((existing) => existing[existing.length - 1].endDay <= segment.startDay);
      if (lane) {
        lane.push(segment);
      } else {
        lanes.push([segment]);
      }
    });
  return lanes;
}

/** Each component drawn to scale across the bundle's days. Days are points, so a check-out meets the next check-in. */
export default function ItineraryTimeline({ segments }: Props) {
  const lastDay = Math.max(MIN_LENGTH, ...segments.map((segment) => segment.endDay));
  const toPercent = (day: number) => `${(day / lastDay) * 100}%`;

  return (
    <Box>
      <Stack spacing={0.75}>
        {intoLanes(segments).map((lane) => (
          <Box key={lane[0].key} position="relative" height={26} borderRadius={1} bgcolor="action.hover">
            {lane.map((segment) => (
              <Box
                key={segment.key}
                position="absolute"
                top={0}
                bottom={0}
                left={toPercent(segment.startDay)}
                width={toPercent(Math.max(segment.endDay - segment.startDay, MIN_LENGTH))}
                borderRadius={1}
                bgcolor={`${segment.color}.main`}
                color={`${segment.color}.contrastText`}
                px={1}
                display="flex"
                alignItems="center"
                overflow="hidden"
                sx={{ boxShadow: (theme) => `inset -1px 0 0 ${theme.palette.background.paper}` }}
              >
                <Typography variant="caption" noWrap fontWeight="medium">
                  {segment.label}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Stack>
      <Stack direction="row" justifyContent="space-between" mt={0.5}>
        <Typography variant="caption" color="text.secondary">
          Day 1 · departure
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Day {Math.ceil(lastDay) + 1}
        </Typography>
      </Stack>
    </Box>
  );
}
